import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, IMAGES } from "@/lib/apiClient";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, CalendarDays, Radio, Loader2, ExternalLink } from "lucide-react";

const field = "w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 text-sm text-white placeholder:text-[#7A708A] outline-none focus:border-gold/50 transition-colors";

export default function LiveSession() {
  const [info, setInfo] = useState(null);
  const [f, setF] = useState({ name: "", contact: "", question: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    api.get("/live-session").then(({ data }) => setInfo(data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.contact) { toast.error("Please share your name and contact."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/live-rsvp", f);
      toast.success(data.message);
      setF({ name: "", contact: "", question: "" });
    } catch { toast.error("Something went wrong. Please call 726-247-5926."); }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-[#05030A] grain text-white relative overflow-hidden" data-testid="live-session-page">
      <div className="absolute inset-0">
        <img src={IMAGES.dove} alt="" className="w-full h-full object-cover opacity-15" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#05030A]/80 via-[#05030A]/90 to-[#05030A]" />
      </div>

      <div className="relative max-w-5xl mx-auto px-5 sm:px-8 py-14">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#B4ADC1] hover:text-gold transition-colors" data-testid="live-back-link">
          <ArrowLeft className="w-4 h-4" /> Back to F.O.L.W.A.C.
        </Link>

        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="mt-12 text-center">
          <span className="section-label inline-flex items-center gap-2"><Radio className="w-3.5 h-3.5" /> Live Weekly Session</span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light mt-5 leading-[1.05]" data-testid="live-title">
            {info?.title || "Ask an Apostolic Minister — Live"}
          </h1>
          <p className="text-lg text-[#B4ADC1] font-light mt-6 max-w-2xl mx-auto leading-relaxed">{info?.details}</p>

          <div className="inline-flex items-center gap-3 glass rounded-full px-6 py-3 mt-8" data-testid="live-schedule">
            <CalendarDays className="w-4 h-4 text-gold" />
            <span className="text-sm font-mono tracking-wider">{info?.schedule || "Schedule coming soon"}</span>
          </div>

          <div className="mt-6">
            {info?.join_url ? (
              <a href={info.join_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-gold text-black font-medium hover:bg-[#e6c14e] transition-colors" data-testid="live-join-btn">
                Join the Live Session <ExternalLink className="w-4 h-4" />
              </a>
            ) : (
              <p className="text-sm font-mono text-[#7A708A]" data-testid="live-join-pending">The join link is posted here before each session begins.</p>
            )}
          </div>
        </motion.div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="glass rounded-3xl p-8 sm:p-10 mt-14 max-w-2xl mx-auto"
          data-testid="live-rsvp-form"
        >
          <h2 className="font-display text-2xl text-white">Reserve your seat</h2>
          <p className="text-sm text-[#B4ADC1] font-light mt-1">We'll send you the join link and a gentle reminder.</p>
          <div className="grid sm:grid-cols-2 gap-4 mt-6">
            <Input className={field} placeholder="First name" value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="rsvp-name" />
            <Input className={field} placeholder="Mobile number or email" value={f.contact} onChange={(e) => set("contact", e.target.value)} data-testid="rsvp-contact" />
          </div>
          <Textarea className={`${field} min-h-24 mt-4`} placeholder="A question you'd like answered (optional)" value={f.question} onChange={(e) => set("question", e.target.value)} data-testid="rsvp-question" />
          <button type="submit" disabled={busy} className="w-full py-4 rounded-full bg-gold text-black font-medium hover:bg-[#e6c14e] transition-colors flex items-center justify-center gap-2 mt-5 disabled:opacity-50" data-testid="rsvp-submit">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} RSVP — It's Free
          </button>
        </motion.form>
      </div>
    </div>
  );
}
